import { useEffect, useState } from "react";
import { Check, Search, Car, UserRound, Sparkles, CircleCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

export function VehicleThumb({ vehicle, catImages, className = "h-16 w-24" }) {
  const src = vehicle.photo || catImages?.[vehicle.category] || "/default-vehicle.jpg";
  return (
    <img src={src} alt={vehicle.registration_number}
      className={`${className} rounded-lg border shrink-0 bg-white ${vehicle.photo ? "object-contain" : "object-contain bg-slate-50"}`} />
  );
}

export function PemanduThumb({ pemandu, className = "h-11 w-11" }) {
  if (pemandu.photo) return <img src={pemandu.photo} alt={pemandu.name} className={`${className} rounded-full object-cover border shrink-0`} />;
  return <span className={`${className} rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[#10056E] shrink-0`}><UserRound className="h-5 w-5" /></span>;
}
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { fmtDate, fmtDateTime } from "../lib/format";

const usageLevel = (n) => {
  if (n >= 4) return { label: "Tinggi", dot: "bg-red-500", txt: "text-red-600" };
  if (n >= 2) return { label: "Sederhana", dot: "bg-amber-500", txt: "text-amber-600" };
  return { label: "Rendah", dot: "bg-emerald-500", txt: "text-emerald-600" };
};

function ScoreBar({ score, max }) {
  const pct = Math.max(4, Math.min(100, Math.round((score / (max || 1)) * 100)));
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <div className="h-1.5 flex-1 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#10056E] to-[#198754]" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-semibold text-slate-500 w-14 text-right">Skor {score}</span>
    </div>
  );
}

function OptionCard({ selected, onClick, testId, children }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`relative w-full text-left border rounded-xl p-3 transition-all ${
        selected ? "border-[#10056E] ring-2 ring-[#10056E]/30 bg-indigo-50/60 shadow-sm" : "border-slate-200 hover:border-[#10056E]/50 hover:bg-slate-50/60"
      }`}>
      {selected && (
        <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#10056E] text-white flex items-center justify-center shadow">
          <Check className="h-3 w-3" />
        </span>
      )}
      {children}
    </button>
  );
}

export function AssignDialog({ booking, open, onOpenChange, isAdmin, onDone }) {
  const [vehicles, setVehicles] = useState([]);
  const [pemandu, setPemandu] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selV, setSelV] = useState("");
  const [selP, setSelP] = useState("");
  const [qV, setQV] = useState("");
  const [qP, setQP] = useState("");
  const [remarks, setRemarks] = useState("");
  const [crossReason, setCrossReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [catImages, setCatImages] = useState({});

  useEffect(() => {
    if (!open) return;
    api.get("/vehicle-categories").then((r) => {
      const m = {};
      r.data.forEach((c) => { if (c.image) m[c.name] = c.image; });
      setCatImages(m);
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !booking) return;
    setSelV(""); setSelP(""); setQV(""); setQP(""); setRemarks(""); setCrossReason("");
    setLoading(true);
    Promise.all([
      api.get(`/bookings/${booking.id}/available-vehicles`),
      api.get(`/bookings/${booking.id}/available-pemandu`),
    ]).then(([v, p]) => { setVehicles(v.data); setPemandu(p.data); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [open, booking]);

  if (!booking) return null;

  const fV = vehicles.filter((v) => `${v.registration_number} ${v.make} ${v.model} ${v.category}`.toLowerCase().includes(qV.toLowerCase()));
  const fP = pemandu.filter((p) => `${p.name} ${p.staff_id || ""}`.toLowerCase().includes(qP.toLowerCase()));
  const maxVScore = Math.max(...vehicles.map((v) => v.score), 1);
  const maxPScore = Math.max(...pemandu.map((p) => p.score), 1);
  const selVehicle = vehicles.find((v) => v.id === selV);
  const selPemandu = pemandu.find((p) => p.id === selP);
  const needCrossReason = !!(booking.unit_id && selVehicle && !selVehicle.same_unit);
  const sugV = vehicles.find((v) => v.suggested);
  const sugP = pemandu.find((p) => p.suggested);

  const useSuggestion = () => {
    if (sugV) setSelV(sugV.id);
    if (sugP) setSelP(sugP.id);
    toast.info("Cadangan sistem dipilih — kenderaan & pemandu paling adil dan sesuai");
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.put(`/bookings/${booking.id}/approve`, {
        vehicle_id: selV, pemandu_id: selP, remarks,
        ...(needCrossReason ? { cross_unit_reason: crossReason } : {}),
      });
      toast.success(isAdmin ? "Tempahan diluluskan — kenderaan & pemandu ditugaskan" : "Kenderaan & pemandu ditugaskan — menunggu kelulusan akhir pentadbir");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-5xl max-h-[92vh] overflow-y-auto p-0">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>Tugaskan Kenderaan & Pemandu</DialogTitle>
            <DialogDescription className="text-xs">
              Sistem menyusun pilihan mengikut <b>skor keadilan & kesesuaian</b> — yang paling kurang digunakan diletakkan di atas supaya agihan tugasan seimbang.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 bg-slate-50 border rounded-xl p-3" data-testid="assign-booking-summary">
            <div className="text-sm min-w-0">
              <p className="font-semibold truncate">{booking.driver_name} → {booking.destination}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {fmtDateTime(booking.start_datetime)} hingga {fmtDateTime(booking.end_datetime)} · <b>{booking.passengers || 1}</b> penumpang · anggaran <b>{booking.estimated_distance} km</b>{booking.unit_name ? ` · ${booking.unit_name}` : ""}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={useSuggestion} disabled={loading || (!sugV && !sugP)}
              className="border-[#198754] text-[#198754] hover:bg-emerald-50 shrink-0" data-testid="assign-use-suggestion-btn">
              <Sparkles className="h-4 w-4 mr-1.5" /> Guna Cadangan Sistem
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 px-6 pt-4">
          <div className="border rounded-xl overflow-hidden flex flex-col">
            <div className="bg-slate-50 border-b px-3 py-2.5 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1.5"><Car className="h-3.5 w-3.5" /> 1 · Pilih Kenderaan ({fV.length})</p>
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="h-8 pl-8 text-xs bg-white" placeholder="Cari plat / model / kategori..." value={qV} onChange={(e) => setQV(e.target.value)} data-testid="assign-vehicle-search" />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto space-y-2 p-3" data-testid="assign-vehicle-list">
              {loading && <p className="text-sm text-muted-foreground border rounded p-3 animate-pulse" data-testid="assign-loading">Memuatkan pilihan...</p>}
              {!loading && fV.length === 0 && <p className="text-sm text-muted-foreground border rounded p-3">Tiada kenderaan tersedia yang memenuhi keperluan.</p>}
              {fV.map((v) => {
                const lvl = usageLevel(v.usage_30d ?? 0);
                return (
                  <OptionCard key={v.id} selected={selV === v.id} onClick={() => setSelV(v.id)} testId={`vehicle-option-${v.registration_number}`}>
                    <div className="flex items-start gap-3">
                      <VehicleThumb vehicle={v} catImages={catImages} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-bold truncate">{v.registration_number} <span className="font-normal text-muted-foreground">— {v.make} {v.model}</span></p>
                          <span className="flex items-center gap-1.5 shrink-0">
                            {v.same_unit && <Badge className="bg-[#10056E] hover:bg-[#10056E] text-[10px]">Bahagian Sama</Badge>}
                            {v.suggested && <Badge className="bg-[#198754] hover:bg-[#198754] text-[10px]">Cadangan Sistem</Badge>}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                          <span>{v.category}{v.unit_name ? ` · ${v.unit_name}` : ""}</span>
                          <span className="inline-flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${lvl.dot}`} /><span className={`font-semibold ${lvl.txt}`}>{lvl.label}</span> · {v.usage_30d ?? 0} tugasan / 30 hari</span>
                        </p>
                        <p className="text-[11px] text-[#10056E] mt-0.5">{v.reason}</p>
                        <ScoreBar score={v.score} max={maxVScore} />
                      </div>
                    </div>
                  </OptionCard>
                );
              })}
            </div>
          </div>

          <div className="border rounded-xl overflow-hidden flex flex-col">
            <div className="bg-slate-50 border-b px-3 py-2.5 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5" /> 2 · Pilih Pemandu ({fP.length})</p>
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="h-8 pl-8 text-xs bg-white" placeholder="Cari nama / no. staf..." value={qP} onChange={(e) => setQP(e.target.value)} data-testid="assign-pemandu-search" />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto space-y-2 p-3" data-testid="assign-pemandu-list">
              {loading && <p className="text-sm text-muted-foreground border rounded p-3 animate-pulse">Memuatkan pilihan...</p>}
              {!loading && fP.length === 0 && <p className="text-sm text-muted-foreground border rounded p-3">Tiada pemandu tersedia pada waktu tersebut.</p>}
              {fP.map((p) => {
                const lvl = usageLevel(p.assignments_30d);
                return (
                  <OptionCard key={p.id} selected={selP === p.id} onClick={() => setSelP(p.id)} testId={`pemandu-option-${p.staff_id || p.id}`}>
                    <div className="flex items-start gap-3">
                      <PemanduThumb pemandu={p} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-bold truncate">{p.name} <span className="font-normal text-muted-foreground">{p.staff_id ? `· ${p.staff_id}` : ""}</span></p>
                          {p.suggested && <Badge className="bg-[#198754] hover:bg-[#198754] text-[10px] shrink-0">Dicadangkan</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                          <span className="inline-flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${lvl.dot}`} /><span className={`font-semibold ${lvl.txt}`}>{lvl.label}</span></span>
                          <span>{p.assignments_30d} tugasan / 30 hari · {p.km_30d} km · terakhir: {p.last_assigned ? fmtDate(p.last_assigned) : "tiada rekod"}</span>
                        </p>
                        <ScoreBar score={p.score} max={maxPScore} />
                      </div>
                    </div>
                  </OptionCard>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-6 pt-4 space-y-3">
          {needCrossReason && (
            <div className="space-y-1.5 border border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="cross-unit-warning">
              <Label className="text-amber-800">Kenderaan dari Bahagian/Unit lain ({selVehicle?.unit_name || "tiada bahagian"}) — sila nyatakan sebab</Label>
              <Textarea value={crossReason} onChange={(e) => setCrossReason(e.target.value)}
                placeholder="Cth: Tiada kenderaan bahagian sendiri yang tersedia pada tarikh tersebut"
                data-testid="cross-unit-reason-input" className="bg-white" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Catatan (pilihan)</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="assign-remarks-input" className="min-h-16" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 mt-4 flex flex-wrap items-center gap-3" data-testid="assign-summary-bar">
          <div className="flex flex-wrap items-center gap-2 text-xs flex-1 min-w-0">
            <span className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1.5 font-semibold ${selVehicle ? "border-[#10056E] text-[#10056E] bg-indigo-50/50" : "border-dashed text-muted-foreground"}`}>
              <Car className="h-3.5 w-3.5" /> {selVehicle ? selVehicle.registration_number : "Belum pilih kenderaan"}
              {selVehicle && <CircleCheck className="h-3.5 w-3.5 text-[#198754]" />}
            </span>
            <span className="text-slate-300">+</span>
            <span className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1.5 font-semibold ${selPemandu ? "border-[#10056E] text-[#10056E] bg-indigo-50/50" : "border-dashed text-muted-foreground"}`}>
              <UserRound className="h-3.5 w-3.5" /> {selPemandu ? selPemandu.name : "Belum pilih pemandu"}
              {selPemandu && <CircleCheck className="h-3.5 w-3.5 text-[#198754]" />}
            </span>
          </div>
          <div className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto bg-[#198754] hover:bg-[#157347]"
              disabled={busy || !selV || !selP || (needCrossReason && !crossReason.trim())}
              onClick={confirm} data-testid="confirm-assign-btn">
              {isAdmin ? "Lulus, Tugaskan Kenderaan & Pemandu" : "Tugaskan & Mohon Kelulusan Admin"}
            </Button>
            {!isAdmin && <p className="text-[10px] text-muted-foreground text-center mt-1">Pentadbir akan menerima emel untuk kelulusan akhir.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
